import type { SpecialQualifyingStatus } from '@iheartjane/data-access';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { formatCurrency, pluralize } from '@iheartjane/utils';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import sumBy from 'lodash-es/sumBy';
import { useRef } from 'react';
import { useNavigate } from 'react-router';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { useKioskSpacing } from '@jane/kiosk/hooks';
import { useMenu } from '@jane/kiosk/providers';
import { transitionStyles } from '@jane/kiosk/util';
import type { Store, StoreSpecial } from '@jane/shared/types';
import { getDiscountedProductsForBundle } from '@jane/shared/util';

import { TermsAndConditions } from '../TermsAndConditions/TermsAndConditions';
import { BundleProductCards } from './BundleProductCards';

import styles from './BundleProgress.module.css';

export interface BundleProgressCart {
  products?: { count: number; id: number; price_id: PriceId | null }[];
}

interface BundleProgressProps {
  cart: BundleProgressCart | undefined;
  special: StoreSpecial;
  status: SpecialQualifyingStatus;
  store: Store;
}

type GlobalProductCount = {
  [key: string]: number;
};

const titleAnimationDuration = 400;

export const BundleProgress = ({
  cart,
  status,
  special,
  store,
}: BundleProgressProps) => {
  const titleRef = useRef<HTMLElement>(null);
  const discountedProductsRef = useRef<HTMLDivElement>(null);
  const rules = special.special_type === 'bundle' ? special.rules : null;
  const navigate = useNavigate();
  const { isIpad } = useKioskSpacing();
  const { visibleTabs } = useMenu();

  const {
    qualified_products = [],
    discounted_products = [],
    qualifying_status,
    discount_total_amount,
  } = status;

  const isApplied = qualifying_status === 'applied';

  if (!status) {
    return null;
  }

  const maxNumberOfDiscountedProducts =
    rules?.settings?.max_number_of_discounted_products ?? 0;

  const discountedProducts = getDiscountedProductsForBundle(
    discounted_products,
    qualified_products
  );

  const threshold = rules?.settings?.threshold_number_of_items_in_cart ?? 0;
  const remainingProducts = threshold - sumBy(qualified_products, 'count');

  const allowQualifiersToBeDiscounted =
    rules?.settings?.allow_discounts_on_required_products ?? false;

  // qualifier & discountable products are identical (the item counts live in
  // settings, so the sections compare directly)
  const perfectMixAndMatch =
    allowQualifiersToBeDiscounted &&
    isEqual(rules?.qualifier, rules?.discountable);

  const globalProductCount =
    cart && cart.products
      ? cart.products.reduce((acc: GlobalProductCount, product) => {
          const key = `${product.id}:${product.price_id}`;

          if (acc[key]) {
            acc[key] += product.count;
          } else {
            acc[key] = product.count;
          }
          return acc;
        }, {})
      : {};

  const discountedProductsThreshold =
    perfectMixAndMatch && !isEmpty(qualified_products)
      ? maxNumberOfDiscountedProducts - threshold
      : maxNumberOfDiscountedProducts;

  const isUnlocked =
    qualifying_status === 'qualified' || qualifying_status === 'applied';

  return (
    <Flex flexDirection="column" alignItems="center" mt={32}>
      <SwitchTransition>
        <CSSTransition
          key={`${isUnlocked}-${isApplied}`}
          timeout={titleAnimationDuration}
          nodeRef={titleRef}
        >
          {(state) => (
            <Typography
              ref={titleRef}
              textAlign="center"
              variant={isApplied ? 'h2-semibold' : 'h3-semibold'}
              mb={8}
              color={isUnlocked ? 'sunset' : 'grays-black'}
              style={{
                transition: `opacity ${titleAnimationDuration}ms ease-in-out`,
                ...transitionStyles({
                  entered: { opacity: 1 },
                  entering: { opacity: 1 },
                  exited: { opacity: 0 },
                  exiting: { opacity: 0 },
                })(state),
              }}
            >
              {isUnlocked
                ? isApplied
                  ? 'You got the deal!'
                  : 'Deal unlocked!'
                : `Unlock savings by adding ${pluralize({
                    noun: 'product',
                    number: remainingProducts,
                  })} to your bag`}
            </Typography>
          )}
        </CSSTransition>
      </SwitchTransition>
      <Flex
        alignItems="center"
        flexDirection="column"
        ml={isIpad ? 48 : 64}
        mr={isIpad ? 48 : 64}
      >
        {isApplied && (
          <Typography textAlign="center" style={{ fontSize: isIpad ? 16 : 22 }}>
            High five! You saved {formatCurrency(discount_total_amount)} with
            this deal!
          </Typography>
        )}
        {!isApplied && (special.description || special.terms) && (
          <>
            <Typography textAlign="center">{special.description}</Typography>
            <TermsAndConditions textAlign="center">
              {special.terms}
            </TermsAndConditions>
          </>
        )}
      </Flex>
      <Flex width="100%" justifyContent="center">
        <Flex
          className={styles['bundleProgress__bundleCardsContainer']}
          pr={isIpad ? 48 : 64}
          pl={isIpad ? 48 : 64}
          pb={isIpad ? 32 : 48}
          pt={24}
          maxWidth="100vw'"
        >
          <BundleProductCards
            products={qualified_products}
            threshold={threshold}
            productType="qualified"
            qualifyingStatus={String(qualifying_status)}
            globalProductCount={globalProductCount}
            perfectMixAndMatch={perfectMixAndMatch}
          />
          <CSSTransition
            in={isUnlocked}
            nodeRef={discountedProductsRef}
            timeout={titleAnimationDuration}
          >
            {(state) => (
              <div
                ref={discountedProductsRef}
                data-testid="bundle-discounted-products"
                style={{
                  display: 'inline-flex',
                  transition: `opacity ${titleAnimationDuration}ms ease-in-out`,
                  ...transitionStyles({
                    entered: { opacity: 1 },
                    entering: { opacity: 0.01 },
                    exited: { opacity: 0, display: 'none' },
                    exiting: { opacity: 0.01 },
                  })(state),
                }}
              >
                <BundleProductCards
                  products={discountedProducts}
                  threshold={discountedProductsThreshold}
                  productType="discounted"
                  qualifyingStatus={String(qualifying_status)}
                  globalProductCount={globalProductCount}
                  perfectMixAndMatch={perfectMixAndMatch}
                />
              </div>
            )}
          </CSSTransition>
        </Flex>
      </Flex>
      {isApplied && (
        <Flex mb={48} width="100%">
          <Button
            full
            label={
              <Typography variant="h3-semibold">Shop more deals</Typography>
            }
            variant="tertiary"
            size="large"
            onClick={() =>
              navigate(`/stores/${store.id}/menu/${visibleTabs[0]}`)
            }
            ml={isIpad ? 48 : 64}
            mr={24}
          />
          <Button
            className={styles['bundleProgress__viewBagButton']}
            full
            label={
              <Typography color="grays-white" variant="h3-semibold">
                View bag
              </Typography>
            }
            variant="primary"
            size="large"
            onClick={() => {
              navigate(`/stores/${store.id}/cart`);
            }}
            mr={isIpad ? 48 : 64}
          />
        </Flex>
      )}
      <div className={styles['bundleProgress__contentDivider']} />
    </Flex>
  );
};
